import React, { useState } from 'react';
import { Col, Row, getItemWithExpiration } from '../../lib/utils';
import { colors } from '../../lib/colors';
import { Body2Regular, Body3Regular, Subtitle1Bold, CaptionLight } from '../../lib/font';
import ColorSquareButton from '../../components/Button/ColorSquareButton';
import WhiteSquareButton from '../../components/Button/WhiteSquareButton';
import {
    useKioskSettings,
    useUpdateKioskSettings,
} from '@/features/kioskSettings/hooks';
import BusinessInfoFooter from '../../components/BusinessInfoFooter';

const KioskSettingsPage = ({ createAlert }: { createAlert: any }) => {
    const [authObject] = useState(getItemWithExpiration('authObject'));
    const centerId = authObject?.centerId;

    const { data, isLoading } = useKioskSettings({ centerId });
    const updateMutation = useUpdateKioskSettings();

    const settings = data?.settings;

    const [welcomeMessage, setWelcomeMessage] = useState('');
    const [showGuide, setShowGuide] = useState(() => localStorage.getItem('guide_kiosk') !== 'hidden');
    const [isEditingMessage, setIsEditingMessage] = useState(false);
    const [copiedKioskUrl, setCopiedKioskUrl] = useState(false);

    const KIOSK_URL = 'https://kiosk.rappomanager.com';

    const handleCopyKioskUrl = async () => {
        try {
            await navigator.clipboard.writeText(KIOSK_URL);
            setCopiedKioskUrl(true);
            setTimeout(() => setCopiedKioskUrl(false), 2000);
        } catch {
            createAlert('복사 실패', '주소 복사에 실패했습니다. 직접 입력해주세요.', true);
        }
    };

    const handleSaveWelcomeMessage = async () => {
        try {
            await updateMutation.mutateAsync({
                centerId,
                body: { welcomeMessage: welcomeMessage.trim() },
            });
            setIsEditingMessage(false);
            createAlert('저장 완료', '환영 메시지가 저장되었습니다.', false);
        } catch {
            createAlert('저장 실패', '환영 메시지 저장에 실패했습니다.', true);
        }
    };

    if (isLoading) {
        return (
            <Col style={{ width: '100%', height: '100%', marginTop: 64, alignItems: 'center', justifyContent: 'center' }}>
                <Body2Regular color={colors.LAVEL_1}>설정을 불러오는 중...</Body2Regular>
            </Col>
        );
    }

    const inputStyle: React.CSSProperties = {
        border: '1px solid #E1E4E6',
        borderRadius: 8,
        padding: '10px 12px',
        fontSize: 14,
        width: '100%',
        outline: 'none',
    };

    return (
        <Col
            style={{
                width: '100%',
                height: '100%',
                backgroundColor: '#F8FAFC',
                marginTop: 64,
                overflow: 'auto',
            }}
        >
            <Col style={{ width: '100%', maxWidth: 720, margin: '0 auto', padding: '40px 32px 56px' }}>
                <Row style={{ alignItems: 'center', gap: 8 }}>
                    <Subtitle1Bold>키오스크 설정</Subtitle1Bold>
                    {!showGuide && (
                        <button onClick={() => { setShowGuide(true); localStorage.removeItem('guide_kiosk'); }}
                            style={{ width: 22, height: 22, borderRadius: '50%', border: '1.5px solid #FDBA74', backgroundColor: '#FFF7ED', color: '#EA580C', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>?</button>
                    )}
                </Row>
                <CaptionLight color={colors.LAVEL_1} style={{ marginTop: 6 }}>
                    키오스크 체크인 기능을 설정합니다
                </CaptionLight>
                {showGuide && (
                    <Col style={{ marginTop: 12, padding: '18px 22px', backgroundColor: '#FFF7ED', borderRadius: 12, border: '1px solid #FDBA74', position: 'relative' }}>
                        <button onClick={() => { setShowGuide(false); localStorage.setItem('guide_kiosk', 'hidden'); }}
                            style={{ position: 'absolute', top: 10, right: 14, background: 'none', border: 'none', fontSize: 16, color: '#FDBA74', cursor: 'pointer' }}>✕</button>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#9A3412', marginBottom: 6 }}>키오스크 설정이란?</span>
                        <Col style={{ gap: 3, fontSize: 13, color: '#9A3412', lineHeight: 1.7 }}>
                            <span>센터 입구에 설치하는 <b>키오스크(태블릿) 체크인 기능</b>을 설정하는 페이지입니다.</span>
                            <span>환영 메시지 등을 커스텀할 수 있습니다.</span>
                        </Col>
                    </Col>
                )}

                {/* 키오스크 접속 안내 */}
                <Col
                    style={{
                        marginTop: 24,
                        padding: '22px 24px',
                        background: 'linear-gradient(135deg, #EFF6FF 0%, #F0F9FF 100%)',
                        borderRadius: 14,
                        border: '1px solid #BFDBFE',
                    }}
                >
                    <Row style={{ alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 20 }}>🖥️</span>
                        <span style={{ fontSize: 15, fontWeight: 700, color: '#1E3A8A' }}>
                            키오스크 접속 방법
                        </span>
                    </Row>
                    <span style={{ fontSize: 13, color: '#1E40AF', marginTop: 8, lineHeight: 1.65 }}>
                        센터 입구에 둘 태블릿(또는 PC)의 브라우저에서 아래 주소로 접속하시면 바로 사용하실 수 있어요. 별도 앱 설치 없이 웹으로 바로 동작합니다.
                    </span>

                    {/* URL 박스 */}
                    <Row
                        style={{
                            marginTop: 14,
                            alignItems: 'center',
                            padding: '12px 14px',
                            backgroundColor: 'white',
                            borderRadius: 10,
                            border: '1px solid #BFDBFE',
                            gap: 10,
                            boxShadow: '0 1px 3px rgba(30,64,175,0.06)',
                        }}
                    >
                        <span
                            style={{
                                flex: 1,
                                fontSize: 14,
                                fontWeight: 700,
                                color: '#1D4ED8',
                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                wordBreak: 'break-all',
                            }}
                        >
                            {KIOSK_URL}
                        </span>
                        <button
                            onClick={handleCopyKioskUrl}
                            style={{
                                padding: '6px 12px',
                                borderRadius: 7,
                                fontSize: 12,
                                fontWeight: 600,
                                backgroundColor: copiedKioskUrl ? '#DCFCE7' : '#FFFFFF',
                                color: copiedKioskUrl ? '#166534' : '#1D4ED8',
                                border: `1px solid ${copiedKioskUrl ? '#86EFAC' : '#BFDBFE'}`,
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                transition: 'all 0.15s',
                            }}
                        >
                            {copiedKioskUrl ? '복사됨 ✓' : '주소 복사'}
                        </button>
                        <button
                            onClick={() => window.open(KIOSK_URL, '_blank')}
                            style={{
                                padding: '6px 12px',
                                borderRadius: 7,
                                fontSize: 12,
                                fontWeight: 600,
                                backgroundColor: '#3B82F6',
                                color: 'white',
                                border: '1px solid #3B82F6',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            새 창 열기 ↗
                        </button>
                    </Row>

                    {/* 단계별 안내 */}
                    <Col style={{ marginTop: 18, gap: 12 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#1E3A8A' }}>이렇게 사용하세요</span>
                        {([
                            {
                                num: '1',
                                title: '크롬(Chrome) 브라우저로 접속 (권장)',
                                desc: (
                                    <>
                                        위 주소를 그대로 입력하거나 "주소 복사" 버튼으로 복사해서 붙여넣어 주세요.
                                        <br />
                                        안정적인 동작을 위해 <b>크롬(Chrome) 브라우저 사용을 권장</b>드려요. 사파리 · 엣지 등 다른 브라우저도 사용 가능합니다.
                                    </>
                                ),
                            },
                            {
                                num: '2',
                                title: '지금 로그인하신 관리자 계정으로 로그인',
                                desc: (
                                    <>
                                        관리자 페이지와 <b>동일한 아이디 · 비밀번호</b>를 그대로 입력하시면 됩니다. 별도의 키오스크 전용 계정은 필요 없어요.
                                        {authObject?.adminId && (
                                            <Row style={{ marginTop: 8, alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                                <span style={{ fontSize: 11, color: '#6B7280' }}>현재 로그인 아이디 :</span>
                                                <span
                                                    style={{
                                                        fontSize: 12,
                                                        fontWeight: 700,
                                                        color: '#1D4ED8',
                                                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                                        backgroundColor: '#EFF6FF',
                                                        border: '1px solid #BFDBFE',
                                                        padding: '2px 8px',
                                                        borderRadius: 6,
                                                    }}
                                                >
                                                    {authObject.adminId}
                                                </span>
                                            </Row>
                                        )}
                                    </>
                                ),
                            },
                            {
                                num: '3',
                                title: '센터 입구에 태블릿 거치 후 체크인 운영',
                                desc: (
                                    <>
                                        회원이 휴대폰 번호로 직접 체크인해요. 체크인·체크아웃 알림이 이 관리자 페이지에 실시간으로 표시됩니다.
                                    </>
                                ),
                            },
                        ] as { num: string; title: string; desc: React.ReactNode }[]).map((step) => (
                            <Row key={step.num} style={{ gap: 12, alignItems: 'flex-start' }}>
                                <span
                                    style={{
                                        flexShrink: 0,
                                        width: 22,
                                        height: 22,
                                        borderRadius: '50%',
                                        backgroundColor: '#3B82F6',
                                        color: 'white',
                                        fontSize: 12,
                                        fontWeight: 700,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginTop: 1,
                                    }}
                                >
                                    {step.num}
                                </span>
                                <Col style={{ flex: 1, gap: 2 }}>
                                    <span style={{ fontSize: 13, fontWeight: 700, color: '#1F2937' }}>{step.title}</span>
                                    <span style={{ fontSize: 12, color: '#4B5563', lineHeight: 1.65 }}>{step.desc}</span>
                                </Col>
                            </Row>
                        ))}
                    </Col>

                    {/* 태블릿 홈 화면 추가 안내 */}
                    <Col
                        style={{
                            marginTop: 16,
                            padding: '14px 16px',
                            backgroundColor: '#FAF5FF',
                            borderRadius: 10,
                            border: '1px solid #DDD6FE',
                            gap: 10,
                        }}
                    >
                        <Row style={{ alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 14 }}>📌</span>
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#6D28D9' }}>
                                태블릿에서 홈 화면 아이콘으로 만들기 (추천)
                            </span>
                        </Row>
                        <span style={{ fontSize: 12, color: '#5B21B6', lineHeight: 1.65 }}>
                            매번 주소를 입력할 필요 없이 앱처럼 한 번에 열 수 있어요.
                        </span>
                        <Col style={{ gap: 6, marginTop: 2 }}>
                            {[
                                <>태블릿 Chrome에서 <b>https://kiosk.rappomanager.com</b> 으로 접속</>,
                                <>주소창 옆 <b>공유 버튼</b> → <b>"Add to home screen" (홈 화면에 추가)</b> 선택</>,
                                <>이제부터는 Chrome 앱이 아니라 <b>홈 화면에 생긴 "라포 키오스크" 아이콘</b>으로 실행하세요</>,
                            ].map((desc, idx) => (
                                <Row key={idx} style={{ gap: 8, alignItems: 'flex-start' }}>
                                    <span
                                        style={{
                                            flexShrink: 0,
                                            width: 18,
                                            height: 18,
                                            borderRadius: '50%',
                                            backgroundColor: '#8B5CF6',
                                            color: 'white',
                                            fontSize: 11,
                                            fontWeight: 700,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginTop: 1,
                                        }}
                                    >
                                        {idx + 1}
                                    </span>
                                    <span style={{ flex: 1, fontSize: 12, color: '#4C1D95', lineHeight: 1.65 }}>{desc}</span>
                                </Row>
                            ))}
                        </Col>
                        <span style={{ fontSize: 11, color: '#7C3AED', backgroundColor: '#F3E8FF', borderRadius: 6, padding: '6px 8px', lineHeight: 1.55 }}>
                            ⚠️ Chrome 앱으로 열면 주소창·탭 같은 브라우저 UI가 보일 수 있어요. <b>꼭 홈 화면 아이콘으로 실행</b>해주세요.
                        </span>
                    </Col>

                    {/* 팁 */}
                    <Col
                        style={{
                            marginTop: 12,
                            padding: '12px 14px',
                            backgroundColor: '#FFFFFF',
                            borderRadius: 10,
                            border: '1px dashed #93C5FD',
                            gap: 6,
                        }}
                    >
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#1E40AF' }}>💡 꿀팁</span>
                        <span style={{ fontSize: 12, color: '#475569', lineHeight: 1.65 }}>
                            · 인터넷이 끊기면 체크인이 동작하지 않으니 와이파이를 안정적으로 연결해 주세요.<br />
                            · 키오스크에선 개인정보 보호를 위해 자동으로 비밀번호 잠금이 걸릴 수 있어요.
                        </span>
                    </Col>
                </Col>

                <Col style={{ marginTop: 32, gap: 20 }}>
                    {/* 환영 메시지 */}
                    <div style={{ padding: 24, backgroundColor: 'white', borderRadius: 12, border: '1px solid #E5E7EB' }}>
                        <Row style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                            <Body2Regular style={{ fontWeight: 600 }}>환영 메시지</Body2Regular>
                            {!isEditingMessage && (
                                <button
                                    onClick={() => {
                                        setWelcomeMessage(settings?.welcomeMessage || '');
                                        setIsEditingMessage(true);
                                    }}
                                    style={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: '#3B82F6',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                    }}
                                >
                                    수정
                                </button>
                            )}
                        </Row>
                        {isEditingMessage ? (
                            <Col style={{ gap: 10 }}>
                                <input
                                    style={inputStyle}
                                    value={welcomeMessage}
                                    onChange={(e) => setWelcomeMessage(e.target.value)}
                                    placeholder="환영합니다!"
                                />
                                <Row style={{ gap: 8, justifyContent: 'flex-end' }}>
                                    <WhiteSquareButton text="취소" size="small" callBack={() => setIsEditingMessage(false)} />
                                    <ColorSquareButton text="저장" size="small" callBack={handleSaveWelcomeMessage} isDisabled={updateMutation.isLoading} />
                                </Row>
                            </Col>
                        ) : (
                            <span style={{ fontSize: 15, color: '#374151' }}>
                                {settings?.welcomeMessage || '환영합니다!'}
                            </span>
                        )}
                    </div>

                </Col>
            </Col>

            {/* 사업자 정보 (카드사 심사용) */}
            <div style={{ width: '100%', marginTop: 40 }}>
                <BusinessInfoFooter />
            </div>
        </Col>
    );
};

export default KioskSettingsPage;
